import React, { FC } from 'react';
import ArrowDownIcon from 'src/shared/material_icons/navigation/arrow_downward.svg';
import ArrowUpIcon from 'src/shared/material_icons/navigation/arrow_upward.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';

interface ComposerTemplateTreeElementContextMenuData {
    onContextMenuItemClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    contextMenu: React.ReactNode;
}

interface ComposerTemplateTreeElementProps {
    name: string;
    icon: React.ReactNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onToggleExpand?: () => void;
    contextMenu?: ComposerTemplateTreeElementContextMenuData;
    selected: boolean;
    shift?: number;
    expanded?: boolean;
    children?: React.ReactNode;
}

interface ComposerTemplateSubTreeElementProps {
    name: string;
    icon: React.ReactNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onToggleExpand?: () => void;
    contextMenu?: ComposerTemplateTreeElementContextMenuData;
    selected: boolean;
    shift?: number;
}

const ComposerTemplateTreeElementContainer = styled.div<{
    shift?: number;
    selected?: boolean;
}>`
    --shift: ${(props) => (props.shift ? `${props.shift * 24}px` : '0px')};
    display: flex;
    align-items: center;
    padding: 4px 8px;
    padding-left: 12px;
    margin-left: ${(props) => (props.shift ? 'var(--shift)' : '0px')};
    background-color: ${(props) => (props.selected ? theme.app.secondary : 'transparent')};
    border: 1px solid ${(props) => (props.selected ? theme.app.secondary : theme.app.borderColor)};
    color: ${(props) => (props.selected ? 'white' : theme.app.textColor)};
    border-radius: 4px;
    cursor: pointer;
    height: 48px;
    min-height: 48px;
    width: ${(props) => (props.shift ? 'calc(100% - var(--shift))' : '100%')};
    user-select: none;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        background-color: ${theme.app.backgroundColor};
        border-color: ${theme.app.secondary};
        opacity: ${(props) => (props.selected ? 1 : 0.8)};
    }
`;

const ComposerTemplateTreeElementHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ComposerTemplateTreeElementLeftContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: calc(100% - 60px); /* Adjusted to account for right container */
    min-width: 0; /* Prevents flex item from overflowing */
`;

const ComposerTemplateTreeElementNameContainer = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ComposerTemplateTreeElementExpanderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 24px;
    cursor: pointer;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const IconContainer = styled.div`
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

export const ComposerTemplateTreeElement: FC<ComposerTemplateTreeElementProps> = ({
    name,
    icon,
    onSelect,
    onDoubleClick,
    onToggleExpand,
    contextMenu,
    selected,
    shift = 0,
    expanded,
    children
}) => {
    const [showContextMenu, setShowContextMenu] = React.useState(false);

    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        if (contextMenu) {
            event.preventDefault();
            event.stopPropagation();
            setShowContextMenu(true);
            contextMenu.onContextMenuItemClick(event);
        }
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onToggleExpand) {
            onToggleExpand();
        }
    };

    return (
        <>
            <ComposerTemplateTreeElementContainer
                shift={shift}
                selected={selected}
                onClick={onSelect}
                onDoubleClick={onDoubleClick}
                onContextMenu={handleContextMenu}
            >
                <ComposerTemplateTreeElementHeader>
                    {shift > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <hr style={{ height: '100%', border: '1px solid ' + theme.app.borderColor, position: 'absolute', left: '-32px' }} />
                        </div>
                    )}
                    <ComposerTemplateTreeElementLeftContainer>
                        {children && (
                            <ComposerTemplateTreeElementExpanderContainer onClick={handleExpandClick}>
                                <IconContainer>
                                    {children &&
                                        (!expanded ? (
                                            <ArrowUpIcon fill={selected ? 'white' : theme.app.textColor} />
                                        ) : (
                                            <ArrowDownIcon fill={selected ? 'white' : theme.app.textColor} />
                                        ))}
                                </IconContainer>
                            </ComposerTemplateTreeElementExpanderContainer>
                        )}
                        {icon && <IconContainer>{icon}</IconContainer>}
                        <ComposerTemplateTreeElementNameContainer title={name}>{name}</ComposerTemplateTreeElementNameContainer>
                    </ComposerTemplateTreeElementLeftContainer>
                </ComposerTemplateTreeElementHeader>
                {showContextMenu && contextMenu && contextMenu.contextMenu}
            </ComposerTemplateTreeElementContainer>

            {expanded && children}
        </>
    );
};

export const ComposerTemplateSubTreeElement: FC<ComposerTemplateSubTreeElementProps> = ({
    name,
    icon,
    shift = 1,
    onSelect,
    onDoubleClick,
    onToggleExpand,
    contextMenu,
    selected
}) => {
    return (
        <ComposerTemplateTreeElement
            name={name}
            icon={icon}
            onSelect={onSelect}
            onDoubleClick={onDoubleClick}
            onToggleExpand={onToggleExpand}
            contextMenu={contextMenu}
            selected={selected}
            shift={shift}
        />
    );
};
